import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTask } from '../contexts/TaskContext';
import { useTimer } from '../contexts/TimerContext';
import { 
  PlusIcon,
  TrashIcon,
  PencilIcon,
  PlayIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

export default function TaskList() {
  const { 
    getTasks, 
    getActiveTasks, 
    getCompletedTasks, 
    addTask, 
    updateTask, 
    deleteTask 
  } = useTask();
  const { startTaskPomodoro } = useTimer();
  
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active', 'completed', 'all'
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    estimatedPomodoros: 1,
    priority: 'medium'
  });

  // Get tasks based on filter
  const filteredTasks = () => {
    switch (filter) {
      case 'active':
        return getActiveTasks();
      case 'completed':
        return getCompletedTasks();
      case 'all':
      default:
        return getTasks();
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        await updateTask(editingTask.id, {
          title: newTask.title,
          description: newTask.description,
          estimatedPomodoros: newTask.estimatedPomodoros,
          priority: newTask.priority
        });
        setEditingTask(null);
      } else {
        await addTask(newTask);
      }
      
      // Reset form
      setNewTask({
        title: '',
        description: '',
        estimatedPomodoros: 1,
        priority: 'medium'
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  // Start editing a task
  const handleEdit = (task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || '',
      estimatedPomodoros: task.estimated_pomodoros,
      priority: task.priority
    });
    setShowForm(true);
  };

  // Start a task
  const handleStartTask = (task) => {
    startTaskPomodoro(task);
  };

  // Mark task as completed
  const handleToggleCompleted = async (task) => {
    const isCompleted = task.status === 'completed';
    await updateTask(task.id, {
      status: isCompleted ? 'in_progress' : 'completed'
    });
  };

  // Get priority class
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto mt-10 px-4">
      {/* Local Storage Info Banner - Updated wording */}
      <div className="bg-tomato-50 p-3 mb-6 rounded-md text-center">
        <p className="text-xs text-tomato-600">
          Tasks stored locally, you will lose data after browser is closed.
          If you want more features, ask Jimmy at 
          <a href="https://jimmy.vibe8.app" target="_blank" rel="noopener noreferrer" className="ml-1 font-medium hover:underline">
            jimmy.vibe8.app
          </a>
        </p>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-display font-bold text-brown-900">Your Tasks</h2>
        <button
          className="btn-primary flex items-center"
          onClick={() => {
            setEditingTask(null);
            setNewTask({
              title: '',
              description: '',
              estimatedPomodoros: 1,
              priority: 'medium'
            });
            setShowForm(!showForm);
          }}
        >
          <PlusIcon className="w-5 h-5 mr-1" />
          {showForm ? 'Cancel' : 'Add Task'}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex space-x-2 mb-4">
        <button
          className={`px-3 py-1 rounded-full text-sm font-semibold ${
            filter === 'active' 
              ? 'bg-tomato-600 text-white' 
              : 'bg-white text-tomato-600 border border-tomato-200'
          }`}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={`px-3 py-1 rounded-full text-sm font-semibold ${
            filter === 'completed' 
              ? 'bg-stem-500 text-white' 
              : 'bg-white text-stem-500 border border-stem-200'
          }`}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
        <button
          className={`px-3 py-1 rounded-full text-sm font-semibold ${
            filter === 'all' 
              ? 'bg-gray-600 text-white' 
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {/* Add/Edit Task Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
              <h3 className="text-lg font-semibold mb-3">
                {editingTask ? 'Edit Task' : 'Add New Task'}
              </h3>
              
              <div className="mb-3">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Task Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-tomato-500 focus:border-tomato-500"
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <textarea
                  id="description"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-tomato-500 focus:border-tomato-500"
                  rows="2"
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="estimatedPomodoros" className="block text-sm font-medium text-gray-700">
                  Estimated Pomodoros
                </label>
                <div className="flex items-center mt-1">
                  <button
                    type="button"
                    onClick={() => setNewTask({ 
                      ...newTask, 
                      estimatedPomodoros: Math.max(1, newTask.estimatedPomodoros - 1) 
                    })}
                    className="px-2 py-1 bg-gray-100 rounded-l-md border border-gray-300"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    id="estimatedPomodoros"
                    value={newTask.estimatedPomodoros}
                    onChange={(e) => setNewTask({ 
                      ...newTask, 
                      estimatedPomodoros: Math.max(1, parseInt(e.target.value) || 1) 
                    })}
                    min="1"
                    className="w-16 px-3 py-1 border-t border-b border-gray-300 text-center"
                  />
                  <button
                    type="button"
                    onClick={() => setNewTask({ 
                      ...newTask, 
                      estimatedPomodoros: newTask.estimatedPomodoros + 1 
                    })}
                    className="px-2 py-1 bg-gray-100 rounded-r-md border border-gray-300"
                  >
                    +
                  </button>
                  
                  <div className="ml-2 flex">
                    {Array.from({ length: Math.min(newTask.estimatedPomodoros, 5) }).map((_, i) => (
                      <div key={i} className="w-5 h-5 rounded-full bg-tomato-500 -mr-1" />
                    ))}
                    {newTask.estimatedPomodoros > 5 && (
                      <div className="ml-1 bg-tomato-100 text-tomato-700 text-xs font-semibold px-2 py-1 rounded-full">
                        +{newTask.estimatedPomodoros - 5}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <div className="flex space-x-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="low"
                      checked={newTask.priority === 'low'}
                      onChange={() => setNewTask({ ...newTask, priority: 'low' })}
                      className="form-radio text-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Low</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="medium"
                      checked={newTask.priority === 'medium'}
                      onChange={() => setNewTask({ ...newTask, priority: 'medium' })}
                      className="form-radio text-yellow-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Medium</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="high"
                      checked={newTask.priority === 'high'}
                      onChange={() => setNewTask({ ...newTask, priority: 'high' })}
                      className="form-radio text-red-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">High</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="mr-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-tomato-600 text-white rounded-md hover:bg-tomato-700"
                >
                  {editingTask ? 'Update Task' : 'Add Task'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task List */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredTasks().map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-lg p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-start">
                <button
                  onClick={() => handleToggleCompleted(task)}
                  className={`mt-1 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 ${
                    task.status === 'completed'
                      ? 'bg-stem-500 border-stem-500 text-white'
                      : 'border-gray-300'
                  }`}
                >
                  {task.status === 'completed' && <CheckIcon className="w-3 h-3" />}
                </button>
                
                <div className="ml-3 flex-grow">
                  <div className="flex justify-between">
                    <h3 className={`text-lg font-semibold ${
                      task.status === 'completed' ? 'line-through text-gray-500' : 'text-brown-900'
                    }`}>
                      {task.title}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStartTask(task)}
                        className="text-tomato-600 hover:text-tomato-800"
                        disabled={task.status === 'completed'}
                      >
                        <PlayIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(task)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-gray-600 hover:text-red-600"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {task.description && (
                    <p className={`text-sm mt-1 ${
                      task.status === 'completed' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {task.description}
                    </p>
                  )}
                  
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">Progress:</span>
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 w-32">
                          <div
                            className="bg-tomato-600 h-2 rounded-full"
                            style={{ 
                              width: `${Math.min(100, (task.completed_pomodoros / task.estimated_pomodoros) * 100)}%` 
                            }}
                          />
                        </div>
                        <span className="ml-2 text-xs text-gray-600">
                          {task.completed_pomodoros}/{task.estimated_pomodoros}
                        </span>
                      </div>
                    </div>
                    
                    <span className={`text-xs px-2 py-1 rounded-full ${getPriorityClass(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {filteredTasks().length === 0 && (
          <div className="text-center p-8">
            <p className="text-gray-500">No tasks found</p>
            <button
              className="mt-2 btn-outline"
              onClick={() => {
                setShowForm(true);
                setFilter('active');
              }}
            >
              Add Your First Task
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
